import {
  ChannelAvatar,
  Flex,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { normalizeNetworkName } from '~publish/helpers/channels'

import type { TakeawayCreatePostType } from '../../graphqlEnums'
import { getTakeawayIcon } from './getTakeawayIcon'
import { ICON_COLOR_CLASSES } from './iconColors'
import { useTakeawayChannels } from './TakeawaysContext'
import type { TakeawayChannel, TakeawayItemCategory } from './types'

import styles from './Takeaways.module.css'

import type { JSX } from 'react'

const MAX_BADGE_AVATARS = 2

function ChannelTooltipContent({
  channels,
}: {
  channels: ReadonlyArray<TakeawayChannel>
}): JSX.Element {
  return (
    <Flex direction="column" gap="xs">
      <Text size="sm" weight="medium">
        Channels
      </Text>
      {channels.map((channel) => (
        <Flex direction="row" align="center" gap="xs" key={channel.id}>
          <ChannelAvatar
            size="xsmall"
            channel={channel.service}
            src={channel.avatar ?? undefined}
            alt=""
            className={styles.tooltipAvatar}
          />
          <Text size="sm" translate="no">
            {channel.name}
          </Text>
        </Flex>
      ))}
    </Flex>
  )
}

export function CategoryBadge({
  category,
  createPostType,
  colorIndex,
}: {
  category: TakeawayItemCategory
  createPostType?: TakeawayCreatePostType | null
  colorIndex: number
}): JSX.Element {
  const colorClass = ICON_COLOR_CLASSES[colorIndex % ICON_COLOR_CLASSES.length]
  const Icon = getTakeawayIcon({ category, createPostType })
  return (
    <div className={`${styles.iconBadge} ${colorClass}`} aria-hidden>
      <Icon size="small" />
    </div>
  )
}

/**
 * The channels a cross-channel takeaway concerns, in the slot the category icon
 * occupies on a single-channel page.
 *
 * The stack is hidden from assistive tech because its overflow badge carries its
 * own "more channel avatars" label. The visually-hidden label replaces it, naming
 * every channel including the ones past the avatar limit.
 */
export function ChannelBadge({
  channelIds,
}: {
  channelIds?: ReadonlyArray<string> | null
}): JSX.Element {
  const channels = useTakeawayChannels(channelIds)
  const channelNames = channels
    .map(
      (channel) => `${normalizeNetworkName(channel.service)} ${channel.name}`,
    )
    .join(', ')

  return (
    <Tooltip
      align="start"
      content={<ChannelTooltipContent channels={channels} />}
    >
      <div className={styles.channelBadge}>
        <ChannelAvatar.Stack
          max={MAX_BADGE_AVATARS}
          size="xsmall"
          stacking="firstOnTop"
          className={styles.channelAvatars}
          aria-hidden
        >
          {channels.map((channel) => (
            <ChannelAvatar
              key={channel.id}
              channel={channel.service}
              src={channel.avatar ?? undefined}
              alt=""
              size="xsmall"
            />
          ))}
        </ChannelAvatar.Stack>
        <VisuallyHidden>
          Relates to: <span translate="no">{channelNames}</span>
        </VisuallyHidden>
      </div>
    </Tooltip>
  )
}
